import { FC, useEffect, useState } from 'react'
import { APP_STORE_URL, GOOGLE_PLAY_URL } from 'config'
import { isMobileDevice } from 'utils/browser'
// eslint-disable-next-line import/no-unresolved
import appStore from 'features/auth/assets/app-store.svg'
// eslint-disable-next-line import/no-unresolved
import googlePlay from 'features/auth/assets/google-play.svg'
import appQr from 'features/auth/assets/login-app-icon.png'
import './style.scss'

const CHROME_DOWNLOAD_URL = 'https://www.google.com/chrome/'

const CONTACTS = [
 { label: '+1 (647) 947-4978', href: 'tel:+16479474978' },
 { label: 'info@agentroof.com', href: 'mailto:info@agentroof.com' },
 { label: 'www.agentroof.com', href: 'https://www.agentroof.com' },
 { label: 'support@agentroof.com', href: 'mailto:support@agentroof.com' },
]

// Full-page block shown when the app is not opened in Chrome for desktop (AR0547)
const BrowserNotSupported: FC = () => {
 const isMobile = isMobileDevice()
 const [copied, setCopied] = useState(false)

 // Reset the "Link copied" label after a short delay
 useEffect(() => {
  if (!copied) return undefined
  const timer = setTimeout(() => setCopied(false), 2000)
  return () => clearTimeout(timer)
 }, [copied])

 const handleCopyLink = async (): Promise<void> => {
  try {
   await navigator.clipboard.writeText(window.location.href)
   setCopied(true)
  } catch {
   // Clipboard can be blocked; the user can still copy from the address bar
  }
 }

 return (
  <div className="browser-warning">
   <header className="bw-brand">
    <div className="bw-welcome">Welcome To</div>
    <div className="bw-brand-row">
     <img className="bw-brand-logo" src="/logo-sm.png" alt="AgentRoof" />
     <span className="bw-brand-name">
      AgentRoof CRM<span className="dot">.</span>
     </span>
    </div>
   </header>

   <main className="bw-card">
    <div className="bw-icon">
     <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
      <rect
       x="2.5"
       y="4"
       width="19"
       height="16"
       rx="2.5"
       stroke="currentColor"
       strokeWidth="1.6"
      />
      <path d="M2.5 8.5h19" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="5.5" cy="6.25" r=".8" fill="currentColor" />
      <circle cx="8" cy="6.25" r=".8" fill="currentColor" />
      <path
       d="M12 11.5v3.5"
       stroke="currentColor"
       strokeWidth="1.8"
       strokeLinecap="round"
      />
      <circle cx="12" cy="17.25" r="1" fill="currentColor" />
     </svg>
    </div>

    <h1 className="bw-title">Browser Not Supported</h1>
    {isMobile ? (
     <p className="bw-text">
      The AgentRoof CRM web app works only on{' '}
      <strong>Google Chrome on a desktop computer</strong>. On this device,
      please use the AgentRoof mobile app.
     </p>
    ) : (
     <>
      <p className="bw-text">
       AgentRoof CRM is only supported on{' '}
       <strong>Google Chrome on desktop</strong>. Please switch to a Chrome
       desktop browser to continue.
      </p>
      <div className="bw-actions">
       <a href={CHROME_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer">
        Download Google Chrome
       </a>
       <span className="bw-sep" aria-hidden="true">
        ·
       </span>
       <button type="button" onClick={handleCopyLink}>
        {copied ? 'Link copied' : 'Copy link to open in Chrome'}
       </button>
      </div>
     </>
    )}

    <div className="bw-divider">
     {isMobile ? 'get the mobile app' : 'or use the mobile app'}
    </div>

    <div className={`bw-app ${isMobile ? 'bw-app-mobile' : ''}`}>
     {/* A QR code is only useful when the phone is a different device */}
     {!isMobile && (
      <div className="bw-app-qr">
       <img src={appQr} alt="Scan to download the AgentRoof app" />
      </div>
     )}
     <div className="bw-app-links">
      {!isMobile && (
       <>
        <p className="bw-app-title">Get the app</p>
        <p className="bw-app-sub">Scan the code or tap a store below</p>
       </>
      )}
      <div className="bw-stores">
       <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
        <img src={appStore} alt="Download on the App Store" />
       </a>
       <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer">
        <img src={googlePlay} alt="Get it on Google Play" />
       </a>
      </div>
     </div>
    </div>
   </main>

   <footer className="bw-contact">
    {CONTACTS.map((c) => (
     <a
      key={c.href}
      href={c.href}
      target={c.href.startsWith('http') ? '_blank' : undefined}
      rel="noopener noreferrer"
     >
      {c.label}
     </a>
    ))}
   </footer>
  </div>
 )
}

export default BrowserNotSupported
